/* eslint-enable no-underscore-dangle,@typescript-eslint/naming-convention */

import { AsyncPipe } from "@angular/common";
import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    Output,
    QueryList,
    ViewChildren,
} from "@angular/core";
import {
    type ClusterIconStyle,
    GoogleMapsModule,
    type MapMarkerClusterer,
} from "@angular/google-maps";
import type { Cluster } from "@googlemaps/markerclustererplus/dist/cluster";
import type { MarkerClusterer } from "@googlemaps/markerclustererplus/dist/markerclusterer";

import { filter, Subscription, switchMap, take } from "rxjs";

import { Coordinates } from "@hermes/api-model-common";
import { LOCALE } from "@hermes/app-core";
import { Locale } from "@hermes/locale";
import {
    PIN_DARK_MARKER_CLUSTERER,
    PIN_WHITE_MARKER_CLUSTERER,
} from "@hermes/map-utils";
import { ExternalLibraryFacade } from "@hermes/states/external-library";

import { MarkerData } from "../../map.type";

import { GoogleSizePipe } from "./google-size.pipe";

@Component({
    selector: "h-map-google-marker",
    templateUrl: "./map-google-marker.component.html",
    imports: [AsyncPipe, GoogleSizePipe, GoogleMapsModule],
})
export class MapGoogleMarkerComponent implements OnDestroy, AfterViewInit {
    /**
     * List of markers to be displayed
     */
    @Input()
    public markers!: MarkerData[];

    /**
     * Activate or not the functionality of marker clusterer
     */
    @Input() public isMarkerClustererActive = false;

    /**
     * Deal with feature on mobile mode only
     */
    @Input()
    public isInMobile = false;

    /*
     * Send to its parent the marker coordinates which is selected
     */
    @Output()
    public hoveredMarkerCoordinates: EventEmitter<Coordinates> =
        new EventEmitter<Coordinates>();

    /*
     * Send to its parent the marker coordinates which is unselected
     */
    @Output()
    public unhoveredMarkerCoordinates: EventEmitter<Coordinates> =
        new EventEmitter<Coordinates>();

    /*
     * Send to its parent the marker coordinates which is clicked
     */
    @Output()
    public clickedMarkerCoordinates: EventEmitter<Coordinates> =
        new EventEmitter<Coordinates>();

    /**
     * Observe map-marker-clusterer component to add addListener
     */
    @ViewChildren("markerClusterer")
    public mapMarkerClustererComponent!: QueryList<MapMarkerClusterer>;

    public markerClustererStyles: ClusterIconStyle[] = [
        {
            url: this.locale.urlPrefix + PIN_DARK_MARKER_CLUSTERER,
            textColor: "#FFFFFF",
            textSize: 14,
            fontWeight: "400",
            width: 35,
            height: 46,
            anchorIcon: [46, 16],
            anchorText: [10, 0],
        },
    ];

    public markerClustererIsLoaded$ =
        this.externalLibraryFacade.isLoaded$("MARKERCLUSTERER");

    private markerClustererListeners: google.maps.MapsEventListener[] = [];

    private subscription: Subscription = new Subscription();

    constructor(
        @Inject(LOCALE) public locale: Locale,
        public externalLibraryFacade: ExternalLibraryFacade,
    ) {}

    public ngAfterViewInit(): void {
        if (!this.isMarkerClustererActive) {
            return;
        }
        // In some case (SPA navigation for exemple) the MarkerClusterer element already exists and `mapMarkerClustererComponent.changes` is not triggered.
        // In this case, no need to listen for changes.
        if (this.mapMarkerClustererComponent.length === 1) {
            this.subscription.add(
                this.mapMarkerClustererComponent.first.markerClustererInitialized.subscribe(
                    (markerClusterer) => {
                        this.addMarkerClustererListener(
                            markerClusterer as unknown as MarkerClusterer,
                        );
                    },
                ),
            );
            return;
        }

        this.subscription.add(
            this.mapMarkerClustererComponent.changes
                .pipe(
                    filter(
                        (elements: QueryList<MapMarkerClusterer>) =>
                            elements.length > 0,
                    ),
                    take(1),
                    switchMap(
                        (elements) => elements.first.markerClustererInitialized,
                    ),
                )
                .subscribe((markerClusterer) => {
                    this.addMarkerClustererListener(
                        markerClusterer as unknown as MarkerClusterer,
                    );
                }),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
        if (this.markerClustererListeners.length > 0) {
            this.markerClustererListeners.forEach((event) => event.remove());
        }
    }

    public mapClicked(marker: MarkerData): void {
        this.clickedMarkerCoordinates.emit(marker.coordinates);
        if (this.isInMobile) {
            this.changeSvgOnHover(marker, true);
        }
    }

    public changeSvgOnHover(marker: MarkerData, mouseOver: boolean): void {
        if (mouseOver) {
            this.hoveredMarkerCoordinates.emit(marker.coordinates);
        } else {
            this.unhoveredMarkerCoordinates.emit(marker.coordinates);
        }
    }

    public trackByMarker(index: number, marker: MarkerData): string {
        return `${index}_${marker?.icon.url}`;
    }

    public addMarkerClustererListener(markerClusterer: MarkerClusterer): void {
        this.markerClustererListeners.push(
            markerClusterer.addListener("mouseover", (cluster: Cluster) => {
                this.onMouseOnMarkerClusterer(
                    cluster,
                    "#000000",
                    this.locale.urlPrefix + PIN_WHITE_MARKER_CLUSTERER,
                );
            }),
            markerClusterer.addListener("mouseout", (cluster: Cluster) => {
                this.onMouseOnMarkerClusterer(
                    cluster,
                    "#FFFFFF",
                    this.locale.urlPrefix + PIN_DARK_MARKER_CLUSTERER,
                );
            }),
        );
    }

    private onMouseOnMarkerClusterer(
        cluster: Cluster,
        color: string,
        pinUrl: string,
    ): void {
        const ngCluster = cluster;
        if (
            /* eslint-disable no-underscore-dangle*/
            ngCluster["clusterIcon_"].div_.childNodes.length >= 3 &&
            ngCluster["clusterIcon_"].div_.childNodes
        ) {
            (
                ngCluster["clusterIcon_"].div_.childNodes[2] as {
                    style: { color: string };
                }
            ).style.color = color;
        }
        ngCluster["clusterIcon_"].div_.firstChild.src = pinUrl;
        /* eslint-enable no-underscore-dangle*/
    }
}
